import { useState, useRef } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { ThemeService } from '../../services/themeService';

interface ThemeImportExportProps {
  className?: string;
}

export function ThemeImportExport({ className = '' }: ThemeImportExportProps) {
  const { theme, updateTheme } = useTheme();
  const [importError, setImportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const exportTheme = () => {
    try {
      const themeData = ThemeService.exportTheme(theme);
      const blob = new Blob([themeData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `theme-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to export theme:', error);
      setImportError('Failed to export theme. Please try again.');
    }
  };

  const copyThemeToClipboard = async () => {
    try {
      const themeData = ThemeService.exportTheme(theme);
      await navigator.clipboard.writeText(themeData);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to copy theme to clipboard:', error);
      setImportError('Failed to copy theme to clipboard.');
    }
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const themeData = JSON.parse(e.target?.result as string);
        await validateAndImportTheme(themeData);
      } catch {
        setImportError('Invalid JSON file.');
      }
    };
    reader.readAsText(file);
  };

  const validateAndImportTheme = async (themeData: unknown) => {
    setImportError(null);
    try {
      const validatedTheme = await ThemeService.importTheme(JSON.stringify(themeData));
      updateTheme(validatedTheme);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setImportSuccess(true);
      setTimeout(() => setImportSuccess(false), 3000);
    } catch (error: unknown) {
      const err = error as { type?: string; details?: { message: string }[]; message?: string };
      if (err.type === 'validation' && err.details) {
        const msgs = err.details.slice(0, 3).map((e) => e.message).join(', ');
        setImportError(`Validation errors: ${msgs}${err.details.length > 3 ? '...' : ''}`);
      } else {
        setImportError(err.message || 'Failed to import theme.');
      }
    }
  };

  const handleTextImport = async (text: string) => {
    try {
      const themeData = JSON.parse(text);
      await validateAndImportTheme(themeData);
    } catch {
      setImportError('Invalid JSON format.');
    }
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${className}`}>
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Import / Export</h3>
            <p className="text-sm text-gray-500">Backup or share your theme</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        <section>
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Export Theme</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={exportTheme} className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download File
            </button>
            <button onClick={copyThemeToClipboard} className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-gray-200">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy to Clipboard
            </button>
          </div>
          {exportSuccess && (
            <div className="mt-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Theme exported successfully!
            </div>
          )}
        </section>

        <div className="relative">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
          <div className="relative flex justify-center"><span className="px-3 bg-white text-sm text-gray-500">or</span></div>
        </div>

        <section>
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Import Theme</h4>
          <div className="mb-4">
            <label className="block">
              <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50/50 transition-colors cursor-pointer">
                <div className="text-center">
                  <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600"><span className="text-primary-600 font-medium">Click to upload</span> or drag and drop</p>
                  <p className="text-xs text-gray-500 mt-1">JSON file only</p>
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileImport} className="hidden" />
            </label>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-2">Or paste theme JSON</label>
            <textarea
              placeholder='{"colors": {...}, "typography": {...}}'
              className="w-full h-28 px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
              onChange={async (e) => { if (e.target.value.trim()) await handleTextImport(e.target.value); }}
            />
          </div>
          {importSuccess && (
            <div className="mt-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Theme imported successfully!
            </div>
          )}
          {importError && (
            <div className="mt-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {importError}
            </div>
          )}
        </section>

        <section className="bg-gray-50 rounded-lg p-4">
          <h5 className="text-sm font-medium text-gray-700 mb-3">Theme Structure</h5>
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
            <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary-400" /><code>colors</code></div>
            <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary-400" /><code>typography</code></div>
            <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary-400" /><code>spacing</code></div>
            <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary-400" /><code>borderRadius</code></div>
            <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary-400" /><code>shadows</code></div>
            <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary-400" /><code>transitions</code></div>
          </div>
        </section>
      </div>
    </div>
  );
}
